/* BEATSEEK — Wall ID Leaderboard pull-out rail (desktop only ≥1100px, left side, top slot). */
import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Target } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const RANK = { 1: '#f7d55c', 2: '#d0d0d0', 3: '#e0a36a' };
const STORAGE_OPEN = 'beatseek_id_board_open';

export const IdLeaderboardRail = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem(STORAGE_OPEN) === '1' : false));
  const [viewport, setViewport] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    fetch(`${API}/api/wall/id-leaderboard`).then(r => r.json()).then(setData).catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_OPEN, open ? '1' : '0');
  }, [open]);

  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (viewport < 1100) return null;

  const PANEL_WIDTH = 240;
  const right = open ? `calc(50% + 336px)` : `calc(50% + 356px)`;
  const leaders = data?.leaders || [];

  return (
    <div
      data-testid="id-leaderboard-rail"
      style={{
        position: 'fixed',
        top: '8vh',
        right,
        transition: 'right 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
        display: 'flex',
        alignItems: 'stretch',
      }}
    >
      {/* Toggle handle (left of the panel) */}
      <button
        data-testid="id-leaderboard-toggle"
        onClick={() => setOpen(o => !o)}
        title={open ? 'Hide the ID leaderboard' : 'Show this month\'s Wall ID leaderboard'}
        style={{
          width: '24px',
          background: 'linear-gradient(180deg, rgba(0,229,255,0.16), rgba(255,45,120,0.16))',
          border: '1px solid rgba(0,229,255,0.38)',
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          borderTopRightRadius: open ? '0' : '12px',
          borderBottomRightRadius: open ? '0' : '12px',
          color: '#00e5ff',
          cursor: 'pointer',
          padding: '14px 0',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
          minHeight: '110px',
          boxShadow: open ? '0 8px 22px rgba(0,0,0,0.45), 0 0 14px 3px rgba(0,229,255,0.4)' : '0 8px 22px rgba(0,0,0,0.45)',
          transition: 'transform 0.2s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s, border-color 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(-4px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.5), 0 0 20px 5px rgba(0,229,255,0.6)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = open ? '0 8px 22px rgba(0,0,0,0.45), 0 0 14px 3px rgba(0,229,255,0.4)' : '0 8px 22px rgba(0,0,0,0.45)'; }}
      >
        {open ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        {!open && (
          <div style={{
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            fontSize: '9px', fontWeight: 800, letterSpacing: '0.18em',
            textTransform: 'uppercase',
            background: 'linear-gradient(135deg, #00e5ff, #ff2d78)',
            WebkitBackgroundClip: 'text', backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            ID · Board
          </div>
        )}
        {!open && <Target size={12} style={{ color: '#00e5ff' }} />}
      </button>

      {/* Panel */}
      <div
        data-testid="id-leaderboard-body"
        style={{
          width: open ? `${PANEL_WIDTH}px` : '0px',
          opacity: open ? 1 : 0,
          overflow: 'hidden',
          maxHeight: open ? '32vh' : '0px',
          overflowY: 'auto',
          background: 'linear-gradient(180deg, #0d0d14 0%, #07070b 100%)',
          borderTop: open ? '1px solid rgba(0,229,255,0.28)' : '1px solid transparent',
          borderRight: open ? '1px solid rgba(0,229,255,0.28)' : '1px solid transparent',
          borderBottom: open ? '1px solid rgba(0,229,255,0.28)' : '1px solid transparent',
          borderLeft: 'none',
          borderTopRightRadius: '14px',
          borderBottomRightRadius: '14px',
          padding: open ? '14px 28px 14px 14px' : '0',
          boxShadow: open ? '10px 0 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,229,255,0.1)' : 'none',
          transition: 'width 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease, padding 0.35s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <Target size={14} style={{ color: '#00e5ff' }} />
          <div style={{
            fontSize: '10px', fontWeight: 800, letterSpacing: '0.16em',
            background: 'linear-gradient(135deg, #00e5ff, #ff2d78)',
            WebkitBackgroundClip: 'text', backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textTransform: 'uppercase',
          }}>
            ID Leaderboard
          </div>
        </div>
        <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '12px', letterSpacing: '0.06em' }}>
          {data?.month || 'This month'} · clips ID'd for other BeatSeekers
        </div>

        {leaders.length === 0 ? (
          <div style={{ padding: '14px', textAlign: 'center', fontSize: '11px', color: '#475569', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '10px' }}>
            <div style={{ marginBottom: '4px' }}>No IDs logged yet this month.</div>
            <div>Hit ID on any Wall clip to take #1.</div>
          </div>
        ) : (
          <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {leaders.map(l => (
              <li
                key={l.user_id}
                data-testid="id-leaderboard-row"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: '9px', background: l.rank <= 3 ? `${RANK[l.rank]}12` : 'rgba(255,255,255,0.03)', border: `1px solid ${l.rank <= 3 ? `${RANK[l.rank]}44` : 'rgba(255,255,255,0.06)'}` }}
              >
                <span style={{ width: 18, fontWeight: 800, fontSize: '11px', color: RANK[l.rank] || '#64748b' }}>#{l.rank}</span>
                {l.avatar
                  ? <img src={l.avatar} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
                  : <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#1a1a2a', color: '#fff', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{(l.username || '?').charAt(0).toUpperCase()}</span>}
                <span style={{ flex: 1, minWidth: 0, fontSize: '11px', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {l.username}{l.streak >= 2 && <span title={`${l.streak} months in a row in the top 3`} style={{ color: '#ff9a3c', marginLeft: 4, fontWeight: 800 }}>🔥{l.streak}</span>}
                </span>
                <span style={{ fontSize: '11px', fontWeight: 800, color: '#00e5ff' }}>{l.ids}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};

export default IdLeaderboardRail;
